import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"
import Alert from "@mui/material/Alert"
import Box from "@mui/material/Box"
import IconButton from "@mui/material/IconButton"
import Link from "@mui/material/Link"
import Paper from "@mui/material/Paper"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Typography from "@mui/material/Typography"
import { useEffect, useState } from "react"
import * as React from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"

import CupEmblem from "../components/CupEmblem"
import Flag from "../components/Flag"
import Locations from "../components/Locations"
import Mii from "../components/Mii"
import PlayerName from "../components/PlayerName"
import TableLoading from "../components/TableLoading"
import TimeDetail from "../components/TimeDetail"
import Wheel from "../components/Wheel"
import environment from "../environment"
import { Cup, cupMap } from "../models/cup"
import { Game } from "../models/game"
import { speedMap } from "../models/speed"
import { trackMap } from "../models/track"
import { getLocationCookie, setLocationCookie } from "../utils/cookie"
import { milliToTime } from "../utils/milli-to-time"
import {
  createCustomSearchParams,
  equalSearchParams,
} from "../utils/search-params"

interface PlayerTitle {
  playerId: string
  playerNnid: string
  playerName: string
  playerAlpha3: string
  playerNation: string
  playerRegion: string
  playerMii: string
  totalTimes: number
  isAlt: boolean
  mainName: string
  mainPid: string
  altNames: string
  altPids: string
  ft150Rank: number
  nft150Rank: number
  ft150RankTotal: number
  nft150RankTotal: number
}

interface PlayerRow {
  cup: Cup
  trackId: number
  trackName: string
  speed: number
  time: string
  motion: boolean
  date: string
  rank: string
  character: number
  variant: number
  miiWeightClass: number
  laps: string[]
  vehicle: number
  tire: number
  glider: number
  coins: string
  shrooms: string
  open: boolean
}

const maxPlayerWidth = 500

export default function Player() {
  const { game, pid } = useParams()
  const cups = cupMap[game as Game]
  const speeds = speedMap[game as Game]
  const tracks = trackMap[game as Game]

  const [searchParams, setSearchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [playerTitle, setPlayerTitle] = useState(null as unknown as PlayerTitle)
  const [playerRows, setPlayerRows] = useState([] as PlayerRow[])
  const [location, setLocation] = useState(
    searchParams.get("location") ?? getLocationCookie()
  )

  useEffect(() => {
    const loc = searchParams.get("location")
    if (loc) {
      setLocationCookie(loc)
      setLocation(loc)
    }
  }, [searchParams])

  useEffect(() => {
    const customSearchParams = createCustomSearchParams({ location: location })
    if (!equalSearchParams(customSearchParams, searchParams)) {
      setSearchParams(customSearchParams)
    }

    fetch(
      `${environment.domain}/api/${game}/players/${pid}?location=${location}`
    )
      .then((res) => res.json() as unknown as any)
      .then((res) => {
        const totalCount = res.totalCount
        const player = res.player as any
        const rows = res.rows as any[]
        let playerRows: PlayerRow[] = []
        cups.forEach((cup) => {
          tracks
            .filter((track) => track.cup === cup.id)
            .forEach((track) => {
              speeds.forEach((speed) => {
                const val = rows.find(
                  (x) => x.tid === track.id && x.speed === speed
                )
                playerRows.push({
                  cup: cup,
                  trackId: track.id,
                  trackName: track.name,
                  speed: speed,
                  time: val?.time ? val.time : "",
                  motion: val?.motion,
                  date: val?.date ? val.date.substring(0, 10) : "",
                  rank: val?.rank ? val.rank : "",
                  character: val?.character,
                  variant: val?.variant,
                  miiWeightClass: val?.mii_weight_class,
                  laps: val?.laps
                    ? val.laps.map((lap: number) => milliToTime(lap))
                    : [],
                  vehicle: val?.vehicle,
                  tire: val?.tire,
                  glider: val?.glider,
                  coins: val?.coins,
                  shrooms: val?.shrooms,
                  open: false,
                })
              })
            })
        })
        const playerTitle: PlayerTitle = {
          playerId: player.pid ?? "",
          playerNnid: player.nnid ?? "",
          playerName: player.name ?? "",
          playerAlpha3: player.alpha_3 ?? "",
          playerNation: player.country ?? "",
          playerRegion: player.region ?? "",
          playerMii: player.mii ?? "",
          totalTimes: totalCount,
          isAlt: player.is_alt,
          mainName: player.main_name ?? "",
          mainPid: player.main_pid ?? "",
          altNames: player.alt_names ?? "",
          altPids: player.alt_pids ?? "",
          ft150Rank: player.ft_150_rank ?? 0,
          nft150Rank: player.nft_150_rank ?? 0,
          ft150RankTotal: player.ft_150_rank_total ?? 0,
          nft150RankTotal: player.nft_150_rank_total ?? 0,
        }
        setPlayerTitle(playerTitle)
        setPlayerRows(playerRows)
        setLoading(false)
      })
  }, [location, game, pid, cups, speeds, tracks])

  const navigate = useNavigate()

  return loading ? (
    <TableLoading />
  ) : !playerTitle.playerId ? (
    <Box>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Player Not Found: {pid}
      </Typography>
      <Typography>
        You must have at least one uploaded top 1000 ghost time. Please contact
        us on{" "}
        <Link
          component="a"
          underline="none"
          href={environment.hyperlinks.discord}
          onClick={(e) => {
            window.open(environment.hyperlinks.discord, "_blank")
            e.preventDefault()
          }}
          sx={{
            verticalAlign: "unset",
            fontFamily: "Quicksand,Azeret Mono",
            fontSize: "1rem",
          }}
        >
          Discord
        </Link>{" "}
        if this is wrong.
      </Typography>
    </Box>
  ) : (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", mb: 3, alignItems: "center" }}>
        <Typography variant="h5">{playerTitle.playerName}</Typography>
        <Box sx={{ flexGrow: 1 }}></Box>
        <Locations
          game={game as Game}
          location={location}
          setLocation={setLocation}
        ></Locations>
      </Box>
      {playerTitle.isAlt ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          This is an alternate account to the main account{" "}
          <Link
            component="a"
            underline="none"
            href={`/${game}/players/${
              playerTitle.mainPid
            }?location=${getLocationCookie()}`}
            onClick={(e) => {
              navigate(
                `/${game}/players/${
                  playerTitle.mainPid
                }?location=${getLocationCookie()}`
              )
              e.preventDefault()
            }}
            sx={{
              verticalAlign: "unset",
              fontFamily: "Quicksand,Azeret Mono",
              fontSize: "14px",
            }}
          >
            <PlayerName name={playerTitle.mainName} />
          </Link>
          . Alternate accounts are not included in player rankings.
        </Alert>
      ) : null}
      <TableContainer
        sx={{ mx: "auto", mb: 3, maxWidth: maxPlayerWidth }}
        component={Paper}
      >
        <Table size="small" aria-label="player">
          <TableBody>
            <TableRow>
              {playerTitle.playerMii ? (
                <TableCell rowSpan={10}>
                  <Box sx={{ textAlign: "center" }}>
                    <Mii data={playerTitle.playerMii} height="128" />
                  </Box>
                </TableCell>
              ) : null}
              <TableCell>Name</TableCell>
              <TableCell>
                <PlayerName name={playerTitle.playerName} />
              </TableCell>
            </TableRow>
            {playerTitle.altPids ? (
              <TableRow>
                <TableCell>Alts</TableCell>
                <TableCell>
                  {playerTitle.altPids.split(",").map((altPid, i) => (
                    <Link
                      style={{ display: "block" }}
                      component="a"
                      underline="none"
                      href={`/${game}/players/${altPid}?location=${getLocationCookie()}`}
                      onClick={(e) => {
                        navigate(
                          `/${game}/players/${altPid}?location=${getLocationCookie()}`
                        )
                        e.preventDefault()
                      }}
                    >
                      <PlayerName name={playerTitle.altNames.split(",")[i]} />
                    </Link>
                  ))}
                </TableCell>
              </TableRow>
            ) : null}
            {playerTitle.mainPid ? (
              <TableRow>
                <TableCell>Main</TableCell>
                <TableCell>
                  <Link
                    style={{ display: "block" }}
                    component="a"
                    underline="none"
                    href={`/${game}/players/${
                      playerTitle.mainPid
                    }?location=${getLocationCookie()}`}
                    onClick={(e) => {
                      navigate(
                        `/${game}/players/${
                          playerTitle.mainPid
                        }?location=${getLocationCookie()}`
                      )
                      e.preventDefault()
                    }}
                  >
                    <PlayerName name={playerTitle.mainName} />
                  </Link>
                </TableCell>
              </TableRow>
            ) : null}
            <TableRow>
              <TableCell>Nnid</TableCell>
              <TableCell>{playerTitle.playerNnid}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Id</TableCell>
              <TableCell>{playerTitle.playerId}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Nation</TableCell>
              <TableCell>
                <Flag
                  alpha3={playerTitle.playerAlpha3}
                  country={playerTitle.playerNation}
                  tooltip={false}
                />
                <span style={{ marginLeft: "10px" }}>
                  {playerTitle.playerNation}
                </span>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Region</TableCell>
              <TableCell>{playerTitle.playerRegion}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Total Times</TableCell>
              <TableCell>
                {playerTitle.totalTimes} / {tracks.length * speeds.length}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>FT Rank</TableCell>
              <TableCell>
                {!playerTitle.isAlt && playerTitle.ft150Rank ? (
                  <Link
                    component="a"
                    underline="none"
                    href={`/${game}/prs?full=true&highlight=${
                      playerTitle.ft150Rank
                    }&location=${getLocationCookie()}&speed=150`}
                    onClick={(e) => {
                      navigate(
                        `/${game}/prs?full=true&highlight=${
                          playerTitle.ft150Rank
                        }&location=${getLocationCookie()}&speed=150`
                      )
                      e.preventDefault()
                    }}
                  >
                    {`${playerTitle.ft150Rank} / ${playerTitle.ft150RankTotal}`}
                  </Link>
                ) : (
                  "???"
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Non-FT Rank</TableCell>
              <TableCell>
                {!playerTitle.isAlt && playerTitle.nft150Rank ? (
                  <Link
                    component="a"
                    underline="none"
                    href={`/${game}/prs?full=false&highlight=${
                      playerTitle.nft150Rank
                    }&location=${getLocationCookie()}&speed=150`}
                    onClick={(e) => {
                      navigate(
                        `/${game}/prs?full=false&highlight=${
                          playerTitle.nft150Rank
                        }&location=${getLocationCookie()}&speed=150`
                      )
                      e.preventDefault()
                    }}
                  >
                    {`${playerTitle.nft150Rank} / ${playerTitle.nft150RankTotal}`}
                  </Link>
                ) : (
                  "???"
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <TableContainer component={Paper}>
        <Table size="small" aria-label="player">
          <TableHead>
            <TableRow>
              <TableCell>Cup</TableCell>
              <TableCell>Track</TableCell>
              {speeds.length > 1 ? <TableCell>Speed</TableCell> : null}
              <TableCell>Time</TableCell>
              <TableCell>Rank</TableCell>
              <TableCell>Upload</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {playerRows.map((row, i) => (
              <React.Fragment>
                <TableRow key={`${row.trackId}${row.speed}`}>
                  {i % (speeds.length * 4) === 0 && (
                    <TableCell rowSpan={speeds.length * 8}>
                      <Box sx={{ textAlign: "center" }}>
                        <CupEmblem cup={row.cup} height="64" />
                      </Box>
                    </TableCell>
                  )}
                  {i % speeds.length === 0 && (
                    <TableCell
                      sx={{ border: speeds.length === 1 ? "none" : null }}
                      rowSpan={speeds.length === 1 ? 1 : speeds.length * 2}
                    >
                      <Link
                        component="a"
                        underline="none"
                        href={`/${game}/trs/${
                          row.trackId
                        }?location=${getLocationCookie()}&speed=${row.speed}`}
                        onClick={(e) => {
                          navigate(
                            `/${game}/trs/${
                              row.trackId
                            }?location=${getLocationCookie()}&speed=${
                              row.speed
                            }`
                          )
                          e.preventDefault()
                        }}
                      >
                        {row.trackName}
                      </Link>
                    </TableCell>
                  )}
                  {speeds.length > 1 ? (
                    <TableCell sx={{ border: "none" }}>
                      <Link
                        component="a"
                        underline="none"
                        href={`/${game}/trs/${
                          row.trackId
                        }?location=${getLocationCookie()}&speed=${row.speed}`}
                        onClick={(e) => {
                          navigate(
                            `/${game}/trs/${
                              row.trackId
                            }?location=${getLocationCookie()}&speed=${
                              row.speed
                            }`
                          )
                          e.preventDefault()
                        }}
                      >
                        {row.speed}cc
                      </Link>
                    </TableCell>
                  ) : null}
                  <TableCell sx={{ border: "none" }}>
                    {row.time ? (
                      <Link
                        component="a"
                        underline="none"
                        href={`/${game}/trs/${row.trackId}?highlight=${
                          row.rank
                        }&location=${getLocationCookie()}&speed=${row.speed}`}
                        onClick={(e) => {
                          navigate(
                            `/${game}/trs/${row.trackId}?highlight=${
                              row.rank
                            }&location=${getLocationCookie()}&speed=${
                              row.speed
                            }`
                          )
                          e.preventDefault()
                        }}
                      >
                        {row.time}
                      </Link>
                    ) : (
                      ""
                    )}
                    {row.motion ? <Wheel /> : ""}
                  </TableCell>
                  <TableCell sx={{ border: "none" }}>{row.rank}</TableCell>
                  <TableCell sx={{ border: "none" }}>{row.date}</TableCell>
                  <TableCell
                    align="right"
                    sx={{ paddingTop: 0, paddingBottom: 0, border: "none" }}
                  >
                    {row.date ? (
                      <IconButton
                        aria-label="expand row"
                        size="small"
                        sx={{ height: "26px", width: "26px" }}
                        onClick={() => {
                          const newPlayerRows = [...playerRows]
                          newPlayerRows[i].open = !row.open
                          setPlayerRows(newPlayerRows)
                        }}
                      >
                        {row.open ? (
                          <KeyboardArrowUpIcon />
                        ) : (
                          <KeyboardArrowDownIcon />
                        )}
                      </IconButton>
                    ) : null}
                  </TableCell>
                </TableRow>
                <TimeDetail
                  open={row.open}
                  trackId={row.trackId}
                  playerId={playerTitle.playerId}
                  game={game as Game}
                  character={row.character}
                  variant={row.variant}
                  miiWeightClass={row.miiWeightClass}
                  laps={row.laps}
                  vehicle={row.vehicle}
                  tire={row.tire}
                  glider={row.glider}
                  coins={row.coins}
                  shrooms={row.shrooms}
                ></TimeDetail>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
